import { ChevronDown, ChevronUp, Eye, Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AutoSaveDropdown } from '@/components/ui/auto-save-dropdown'
import { AutoSaveInput } from '@/components/ui/auto-save-input'
import { AutoSaveTextarea } from '@/components/ui/auto-save-textarea'
import { AutoSaveToggle } from '@/components/ui/auto-save-toggle'
import { Button } from '@/components/ui/button'
import { AUTO_SAVE_DEBOUNCE_MS } from '@/hooks/use-auto-save'
import { type RouterInputs, type RouterOutputs, trpc } from '@/lib/trpc'
import { errorCode } from '@/lib/trpc-error'
import { toastError, toastSuccess } from '@/lib/utils/toast-helpers'

/**
 * One assertion, as the router returns it (LG-931).
 *
 * Derived from the router output rather than hand-declared, so a change to the
 * procedure surfaces here as a type error instead of at runtime.
 */
export type SDAssertion =
  RouterOutputs['admin']['content']['listSystemDesignAssertions']['assertions'][number]

/**
 * The text columns a card edits in place.
 *
 * Intersected with the mutation's own input rather than hand-listed. The
 * computed `[field]: value` key below degrades the mutation literal to an
 * index signature, so without this nothing ties these names to the router: a
 * field renamed there would be stripped by Zod, the response would carry the
 * old text, and the merge would restore it while the input still rendered as
 * saved -- the exact silent lost edit this round is about.
 */
type UpdateAssertionInput =
  RouterInputs['admin']['content']['updateSystemDesignAssertion']

type SavableTextField = Extract<
  keyof UpdateAssertionInput & keyof SDAssertion,
  'whatWeCheck' | 'expected' | 'insufficient'
>

/**
 * Build the one-field patch a text save sends: `field` typed so that `value` is
 * checked against what the procedure accepts for it.
 *
 * `Exclude<..., undefined>` rather than `NonNullable`: `undefined` is the "not
 * sent" sentinel this patch exists to avoid, but `insufficient` is `nullish()`
 * on the router and an explicit `null` means "clear the column", which
 * `NonNullable` would make unrepresentable.
 */
function fieldPatch<F extends SavableTextField>(
  field: F,
  value: Exclude<UpdateAssertionInput[F], undefined>,
): Pick<UpdateAssertionInput, F> {
  // The cast is what a computed key costs. `{ [field]: value }` degrades to a
  // string index signature, and an index signature satisfies an all-optional
  // target vacuously -- the checker walks the *target's* properties, finds none
  // of them on the source, and skips each one because it is optional. So an
  // annotation here would verify the field names and never the values. The
  // generic does the checking instead, at the call site; the cast only
  // restates what it proved.
  //
  // That holds only while `F` is a single literal, which every caller today
  // satisfies and the signature does not enforce. Instantiated with the union,
  // `value` widens to the union of all three columns while `Pick` collapses to
  // three independent optionals, and `null` for a field the router requires
  // would type check and come back a BAD_REQUEST. A caller that holds the
  // field in a variable needs the pair correlated first.
  const patch: Record<string, unknown> = { [field]: value }
  return patch as Pick<UpdateAssertionInput, F>
}

/**
 * The same patch against the row type, for the local draft a keystroke holds.
 *
 * Separate from `fieldPatch` because the two types are not the same thing: the
 * router input says what may be *sent*, the row says what a bank member *is*.
 */
function rowPatch<F extends SavableTextField>(
  field: F,
  value: SDAssertion[F],
): Pick<SDAssertion, F> {
  return { [field]: value } as Pick<SDAssertion, F>
}

/**
 * Keyed by the union rather than listed, so adding a value to the pg enum is a
 * compile error here instead of a row that renders as the empty placeholder.
 */
const GRADED_FROM_LABELS: Record<SDAssertion['gradedFrom'], string> = {
  board: 'Board only',
  board_and_conversation: 'Board + conversation',
}

const GRADED_FROM_OPTIONS = Object.entries(GRADED_FROM_LABELS).map(
  ([value, label]) => ({ value, label }),
)

/**
 * Narrow the dropdown's plain `string` back to the enum by own-property
 * lookup, not a cast. `in` walks the prototype chain, which would wave
 * `'toString'` and `'constructor'` through as enum members.
 */
function toGradedFrom(value: string): SDAssertion['gradedFrom'] | null {
  return Object.hasOwn(GRADED_FROM_LABELS, value)
    ? (value as SDAssertion['gradedFrom'])
    : null
}

interface SDAssertionsSectionProps {
  problemId: string
}

export function SDAssertionsSection({ problemId }: SDAssertionsSectionProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  /**
   * Add, delete and reorder are in flight -- the mutations that change which
   * rows exist and in what order. Named for what it actually excludes: the
   * per-field saves deliberately do not set it, so this is not general mutual
   * exclusion, and a future handler must not read it as though it were.
   */
  const [membershipSaving, setMembershipSaving] = useState(false)
  /**
   * The same flag, readable synchronously.
   *
   * The state alone cannot serialize anything: two clicks dispatched in one
   * React batch both run against the render closure that set it, so both read
   * `false` and both proceed. Across renders the `disabled` attribute already
   * stops the second click, which left the handler guard enforcing nothing in
   * the only case it was written for.
   */
  const membershipSavingRef = useRef(false)
  const setMembership = useCallback((value: boolean) => {
    membershipSavingRef.current = value
    setMembershipSaving(value)
  }, [])

  /**
   * The bank as rendered.
   *
   * Local rather than read straight off the query cache because every text
   * field is a controlled input: with nowhere to put a keystroke before it is
   * saved, the debounce would have nothing to debounce and the fields would be
   * uneditable.
   */
  const [assertions, setAssertions] = useState<SDAssertion[]>([])
  /**
   * The same rows, readable synchronously. Handlers read this rather than the
   * render closure so a click landing right after a keystroke still operates on
   * the rows currently on screen.
   */
  const assertionsRef = useRef<SDAssertion[]>([])
  const adopt = useCallback((next: SDAssertion[]) => {
    assertionsRef.current = next
    setAssertions(next)
  }, [])

  const utils = trpc.useUtils()
  const listQuery = trpc.admin.content.listSystemDesignAssertions.useQuery({
    problemId,
  })
  const createMutation =
    trpc.admin.content.createSystemDesignAssertion.useMutation()
  const updateMutation =
    trpc.admin.content.updateSystemDesignAssertion.useMutation()
  const deleteMutation =
    trpc.admin.content.deleteSystemDesignAssertion.useMutation()
  const reorderMutation =
    trpc.admin.content.reorderSystemDesignAssertions.useMutation()

  /**
   * Hydrate from the server once per problem, not on every new query identity.
   *
   * React Query refetches on window focus once the 30s `staleTime` is up, so an
   * unguarded adopt would overwrite whatever is mid-debounce the moment the
   * author alt-tabs away and back. Every mutation returns the authoritative
   * bank and pushes it into the cache below, so there is nothing this effect
   * needs to re-run for. Same guard the problem editor uses to hydrate itself.
   */
  const hydratedProblemIdRef = useRef<string | null>(null)
  const serverAssertions = listQuery.data?.assertions
  useEffect(() => {
    if (!serverAssertions) return
    if (hydratedProblemIdRef.current === problemId) return
    hydratedProblemIdRef.current = problemId
    adopt(serverAssertions)
  }, [serverAssertions, problemId, adopt])

  /**
   * Keep the query cache level with what we just wrote.
   *
   * Without this the cached bank stays pre-mutation for `staleTime`, so
   * unmounting and remounting the section (or the parent flipping its
   * `problemId &&` guard) rehydrates a just-deleted row back onto the screen.
   */
  const syncCache = useCallback(async () => {
    // Cancel before writing. A list fetch already on the wire resolves *after*
    // this `setData` and React Query lets it replace what we wrote, which puts
    // the pre-mutation bank back -- and the cache is exactly what the next
    // mount hydrates from. Two ways in: a mutation finishing before the
    // initial load does, and a focus refetch started past `staleTime` landing
    // after a debounced save.
    await utils.admin.content.listSystemDesignAssertions.cancel({ problemId })
    // Takes no bank, and writes whatever is on screen at the moment the cancel
    // resolves. Awaiting un-serialized these writes: two saves settling close
    // together each await their own cancel, and a cancel with a fetch to abort
    // resolves later than one with nothing to do, so the earlier save can
    // reach `setData` second. A payload captured before the await would then
    // put its older bank in the cache, and a remount would hand the author
    // back a value the database had already saved past. The ref is written
    // synchronously by `adopt`, which every caller here runs first.
    utils.admin.content.listSystemDesignAssertions.setData(
      { problemId },
      { assertions: assertionsRef.current },
    )
  }, [utils, problemId])

  /**
   * Adopt the bank's membership and order from a server response, keeping any
   * text the author is still typing.
   *
   * Add, delete and reorder change which rows exist and in what order; they do
   * not change any column of the rows that stay. Adopting the response
   * wholesale would snap every in-flight edit back to the database's copy --
   * the same clobber the per-field handlers avoid, and worse here, because
   * `useAutoSave` still holds the typed value and would go on to persist it
   * while the screen showed the old one, with hydrate-once ensuring nothing
   * ever resynced.
   *
   * So a row we already hold keeps every one of its own columns and takes only
   * `order` from the response. That covers the edits these mutations cannot see
   * coming: the text fields, but equally `gradedFrom` and `isBaseline`, whose
   * saves are not serialized against this one and whose response can therefore
   * predate them. A row we have not seen before (a just-created one) is taken
   * entire.
   *
   * The merged bank is what goes to the cache as well. Sending the raw response
   * there instead would leave the cache disagreeing with the screen about every
   * row being edited -- and the cache is exactly what a remount hydrates from.
   */
  const adoptMembership = useCallback(
    async (next: SDAssertion[]) => {
      const drafts = new Map(assertionsRef.current.map((a) => [a.id, a]))
      const merged = next.map((row) => {
        const draft = drafts.get(row.id)
        return draft ? { ...draft, order: row.order } : row
      })
      // This bank is hydrated, whatever the list query is still doing. A
      // mutation can land before the initial load resolves -- "Add assertion"
      // renders before the list does -- and without this the effect below
      // would see its first response afterwards, believe it had never
      // hydrated, and replace the bank with the pre-mutation one.
      //
      // Only the membership path needs to say so. A per-field save cannot be
      // the first thing to land: its inputs live in an expanded row, which
      // means a row already exists, which means either the load resolved or a
      // create came through here.
      hydratedProblemIdRef.current = problemId
      adopt(merged)
      await syncCache()
    },
    [adopt, syncCache, problemId],
  )

  const baselineCount = useMemo(
    // Counted off the rows rather than tracked separately: the server enforces
    // at most one, and this only exists to warn an author who has set none.
    () => assertions.filter((a) => a.isBaseline).length,
    [assertions],
  )

  /** A keystroke, held locally until the debounce fires. */
  const handleLocalUpdate = useCallback(
    <F extends SavableTextField>(
      assertionId: string,
      field: F,
      value: SDAssertion[F],
    ) => {
      const next = assertionsRef.current.map((a) =>
        a.id === assertionId ? { ...a, ...rowPatch(field, value) } : a,
      )
      adopt(next)
    },
    [adopt],
  )

  /**
   * Save one text field.
   *
   * One field in, one field out: adopting the whole returned bank here would
   * snap every other debounced input back to the database's value, erasing
   * anything typed while this save was in flight. The row is only touched if it
   * still holds the value that was sent, so a later keystroke wins over a
   * slower response.
   */
  const handleSaveField = useCallback(
    async <F extends SavableTextField>(
      assertionId: string,
      field: F,
      value: Exclude<UpdateAssertionInput[F], undefined>,
    ) => {
      try {
        const result = await updateMutation.mutateAsync({
          problemId,
          assertionId,
          ...fieldPatch(field, value),
        })
        const saved = result.assertions.find((a) => a.id === assertionId)
        if (!saved) return
        const next = assertionsRef.current.map((a) =>
          a.id === assertionId && Object.is(a[field], value)
            ? { ...a, ...rowPatch(field, saved[field]) }
            : a,
        )
        // The merged bank goes to both, for the reason `adoptMembership`
        // spells out: a remount hydrates from the cache, so pushing the raw
        // response there would hand the next instance the database's copy of
        // every row still mid-debounce on this one, and the author's next
        // keystroke would overwrite a save that had already succeeded.
        adopt(next)
        await syncCache()
      } catch (error) {
        // A row deleted while its debounce was still pending flushes on
        // unmount into a 404. The delete already reported itself; a second,
        // contradictory "failed to save" for the same action is noise.
        //
        // Only when the row is genuinely gone from our own bank. Returning
        // normally tells `useAutoSave` the save succeeded -- it advances its
        // last-saved value and lights the "saved" indicator -- so swallowing
        // every 404 would report success for text that was never persisted if
        // the row were retired in another tab while this one was being typed
        // into.
        if (
          errorCode(error) === 'NOT_FOUND' &&
          !assertionsRef.current.some((a) => a.id === assertionId)
        ) {
          return
        }
        // One save policy for every field, rather than the same try/catch
        // pasted at each call site. Rethrown so the control still shows its
        // own error border.
        toastError(error, 'Failed to save')
        throw error
      }
    },
    [updateMutation, problemId, adopt, syncCache],
  )

  /**
   * Save the graded-from mode.
   *
   * Merges the one field it changed, for the same reason the text fields do:
   * the response carries the database's copy of every other row, which is
   * stale for anything the author is mid-typing.
   */
  const handleSaveGradedFrom = useCallback(
    async (assertionId: string, value: string) => {
      const gradedFrom = toGradedFrom(value)
      if (!gradedFrom) return
      try {
        const result = await updateMutation.mutateAsync({
          problemId,
          assertionId,
          gradedFrom,
        })
        const saved = result.assertions.find((a) => a.id === assertionId)
        if (!saved) return
        const next = assertionsRef.current.map((a) =>
          a.id === assertionId ? { ...a, gradedFrom: saved.gradedFrom } : a,
        )
        // Merged to both, like the text fields.
        adopt(next)
        await syncCache()
      } catch (error) {
        toastError(error, 'Failed to save')
        throw error
      }
    },
    [updateMutation, problemId, adopt, syncCache],
  )

  /**
   * Toggle the baseline mark.
   *
   * Takes the `isBaseline` column of every row the server returned, and
   * nothing else. Promoting a check clears whichever row previously held the
   * mark, server-side, so re-rendering only this row would show two baselines
   * -- but that is the only column the other rows changed, and adopting the
   * response wholesale would take their in-flight text along with it.
   */
  const handleSaveBaseline = useCallback(
    async (assertionId: string, isBaseline: boolean) => {
      try {
        const result = await updateMutation.mutateAsync({
          problemId,
          assertionId,
          isBaseline,
        })
        const marked = new Map(
          result.assertions.map((a) => [a.id, a.isBaseline]),
        )
        const next = assertionsRef.current.map((a) =>
          marked.has(a.id)
            ? { ...a, isBaseline: marked.get(a.id) ?? false }
            : a,
        )
        // Merged to both, like the text fields.
        adopt(next)
        await syncCache()
      } catch (error) {
        // The server rejects a second baseline with a CONFLICT naming the rule,
        // which is more useful than a generic failure: the fix is to clear the
        // other one first.
        toastError(error, 'Failed to update baseline')
        throw error
      }
    },
    [updateMutation, problemId, adopt, syncCache],
  )

  const handleAdd = useCallback(async () => {
    // Serialized with the other two membership mutations, synchronously, for
    // the reason `handleMove` gives: the `disabled` attribute cannot refuse a
    // click dispatched in the same batch as the one that set the flag.
    if (membershipSavingRef.current) return
    // Not before the bank is known. `before` would be `[]`, so every row the
    // response carries looks new, and the row we expand below is then whichever
    // sorts first -- an existing check the author proceeds to type over. Covers
    // `problemId` changing under a mounted section too, where `assertionsRef`
    // still holds the previous problem's rows.
    if (!listQuery.data) return
    setMembership(true)
    const before = assertionsRef.current
    try {
      const result = await createMutation.mutateAsync({
        problemId,
        whatWeCheck: 'New check',
        expected: '',
      })
      await adoptMembership(result.assertions)
      // Open the new row so the author lands in the fields they have to fill.
      // Found by diffing against the rows we had, never by taking the last
      // element — `order` is author-chosen, not creation order.
      const created = result.assertions.find(
        (a) => !before.some((existing) => existing.id === a.id),
      )
      if (created) setExpandedId(created.id)
      toastSuccess('Added assertion')
    } catch (error) {
      toastError(error, 'Failed to add assertion')
    } finally {
      setMembership(false)
    }
  }, [
    createMutation,
    problemId,
    adoptMembership,
    listQuery.data,
    setMembership,
  ])

  const handleDelete = useCallback(
    async (assertionId: string) => {
      // Serialized like add and move. Without this, two Remove clicks in one
      // batch both fire, their responses can settle out of order, and the
      // loser's adopt writes a membership the server no longer holds -- which
      // hydrate-once then never corrects.
      if (membershipSavingRef.current) return
      setMembership(true)
      try {
        const result = await deleteMutation.mutateAsync({
          problemId,
          assertionId,
        })
        await adoptMembership(result.assertions)
        // Through the updater: `expandedId` in this closure is whatever was
        // open when Remove was clicked, and the header buttons stay live
        // during the round trip, so a plain read would collapse a different
        // row the author opened while the delete was on the wire.
        setExpandedId((prev) => (prev === assertionId ? null : prev))
        toastSuccess('Removed assertion')
      } catch (error) {
        toastError(error, 'Failed to remove assertion')
      } finally {
        setMembership(false)
      }
    },
    [deleteMutation, problemId, adoptMembership, setMembership],
  )

  const handleMove = useCallback(
    async (assertionId: string, direction: -1 | 1) => {
      // Serialized with add and delete, the other two mutations that change
      // membership. Two arrow clicks inside one round trip would otherwise
      // race: the responses can settle out of order and the loser's adopt
      // writes an order the server no longer holds, which hydrate-once then
      // never corrects.
      if (membershipSavingRef.current) return
      const current = assertionsRef.current
      const index = current.findIndex((a) => a.id === assertionId)
      const target = index + direction
      if (index === -1 || target < 0 || target >= current.length) return

      const reordered = [...current]
      const [moved] = reordered.splice(index, 1)
      reordered.splice(target, 0, moved)

      // Applied before the await, so the ref reflects this click: two quick
      // clicks on the same arrow would otherwise both read the pre-move array
      // and send the same order twice, moving the row one place for two clicks.
      adopt(reordered)
      setMembership(true)

      try {
        const result = await reorderMutation.mutateAsync({
          problemId,
          assertionIds: reordered.map((a) => a.id),
        })
        // The server re-reads and returns the bank in its own order, which is
        // the one the grader will see.
        await adoptMembership(result.assertions)
      } catch (error) {
        // Put the rows back where they were: the optimistic splice above never
        // reached the database. Only the ordering is restored, and over the
        // rows as they stand now -- `current` is a snapshot from before the
        // round trip, so anything typed while the reorder was in flight is
        // newer than it.
        const restored = new Map(assertionsRef.current.map((a) => [a.id, a]))
        adopt(current.flatMap((row) => restored.get(row.id) ?? []))
        // The cache too, like every other adopt in this file. This is the one
        // path that writes the screen before the server agrees, so the failed
        // order can already have reached the cache: a per-field save is not
        // serialized against membership mutations, and a debounce settling
        // inside this round trip runs `syncCache`, which writes whatever the
        // ref holds -- the optimistic splice. Restoring only the screen would
        // leave the rejected order in the cache a remount hydrates from, and
        // hydrate-once would then never correct it.
        await syncCache()
        toastError(error, 'Failed to reorder assertions')
      } finally {
        setMembership(false)
      }
    },
    [
      reorderMutation,
      problemId,
      adopt,
      adoptMembership,
      setMembership,
      syncCache,
    ],
  )

  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <span className="text-foreground text-sm font-medium">
            Assertions
          </span>
          <p className="text-muted-foreground mt-1 text-xs">
            What the grader looks for. Every check is hidden from the candidate
            mid-session except the baseline.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={membershipSaving || !listQuery.data}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add assertion
        </Button>
      </div>

      {baselineCount === 0 && assertions.length > 0 && (
        <p className="text-muted-foreground mb-3 text-xs">
          No baseline check set. Without one, the candidate sees nothing at all
          mid-session.
        </p>
      )}

      {listQuery.error && !listQuery.data ? (
        // Ahead of the empty state on purpose: a failed load rendered as "no
        // assertions yet" invites the author to re-author a bank that already
        // exists.
        //
        // Only when the load never produced a bank, though. This query
        // refetches on window focus, and React Query keeps `data` while setting
        // `error` on a failed background refetch -- so preempting
        // unconditionally would replace a bank the author is editing with this
        // message on a blip, unmounting the expanded panel and putting their
        // drafts out of reach. Keyed off `data` and not off the row count,
        // because a problem whose bank is legitimately empty has no rows either
        // and must keep its empty state through the same blip.
        <p className="text-destructive text-sm">
          Failed to load assertions. Reload to try again.
        </p>
      ) : listQuery.isLoading ? (
        <p className="text-muted-foreground text-sm">Loading assertions…</p>
      ) : assertions.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No assertions yet. Add the checks this problem is graded on.
        </p>
      ) : (
        <div className="space-y-2">
          {assertions.map((assertion, index) => {
            const isExpanded = expandedId === assertion.id
            // The author can empty `whatWeCheck`, and three icon buttons in a
            // list of identical rows announcing as "Move  up" is worse than a
            // placeholder name.
            const label = assertion.whatWeCheck || 'Untitled check'
            const panelId = `sd-assertion-panel-${assertion.id}`
            return (
              <div
                key={assertion.id}
                className="border-border rounded-md border"
              >
                <div className="flex items-center gap-2 p-3">
                  <div className="flex flex-col">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4"
                      // `membershipSaving` too, not just the end of the bank:
                      // the handler refuses while another membership mutation
                      // is in flight, and an arrow that takes the click and
                      // does nothing reads as a dead control.
                      disabled={membershipSaving || index === 0}
                      onClick={() => handleMove(assertion.id, -1)}
                      aria-label={`Move ${label} up`}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4"
                      disabled={
                        membershipSaving || index === assertions.length - 1
                      }
                      onClick={() => handleMove(assertion.id, 1)}
                      aria-label={`Move ${label} down`}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </div>

                  <button
                    type="button"
                    className="flex flex-1 items-center gap-2 text-left"
                    aria-expanded={isExpanded}
                    // Only while the panel is actually in the DOM: a
                    // reference to an absent id is an invalid ARIA relationship
                    // that some screen readers announce as broken.
                    aria-controls={isExpanded ? panelId : undefined}
                    onClick={() =>
                      setExpandedId(isExpanded ? null : assertion.id)
                    }
                  >
                    <span className="text-foreground text-sm">{label}</span>
                    {assertion.isBaseline && (
                      <span
                        className="text-muted-foreground inline-flex items-center gap-1 text-xs"
                        title="Baseline check — visible mid-session"
                      >
                        <Eye className="h-3 w-3" />
                        Baseline
                      </span>
                    )}
                  </button>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={membershipSaving}
                    onClick={() => handleDelete(assertion.id)}
                    aria-label={`Remove ${label}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {isExpanded && (
                  // Every field below opts into `flushOnUnmount`: this panel
                  // unmounts its inputs when the card collapses, and a
                  // debounced save is dropped on unmount by default, so typing
                  // and then collapsing inside the window would lose the edit
                  // silently -- while the collapsed header went on showing it.
                  <div
                    id={panelId}
                    className="border-border space-y-3 border-t p-3"
                  >
                    <div>
                      <label
                        htmlFor={`sd-assertion-what-${assertion.id}`}
                        className="text-muted-foreground mb-1 block text-sm font-medium"
                      >
                        What we check
                      </label>
                      <AutoSaveInput
                        id={`sd-assertion-what-${assertion.id}`}
                        type="text"
                        value={assertion.whatWeCheck}
                        onChange={(value) =>
                          handleLocalUpdate(assertion.id, 'whatWeCheck', value)
                        }
                        onSave={(value) =>
                          handleSaveField(assertion.id, 'whatWeCheck', value)
                        }
                        debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
                        flushOnUnmount
                        placeholder="Cache sits in front of reads"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor={`sd-assertion-expected-${assertion.id}`}
                        className="text-muted-foreground mb-1 block text-sm font-medium"
                      >
                        Expected
                      </label>
                      <AutoSaveTextarea
                        id={`sd-assertion-expected-${assertion.id}`}
                        value={assertion.expected}
                        onChange={(value) =>
                          handleLocalUpdate(assertion.id, 'expected', value)
                        }
                        onSave={(value) =>
                          handleSaveField(assertion.id, 'expected', value)
                        }
                        debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
                        flushOnUnmount
                        placeholder="Exactly what the AI should look for, on the board and in the transcript."
                      />
                    </div>

                    <div>
                      <label
                        htmlFor={`sd-assertion-insufficient-${assertion.id}`}
                        className="text-muted-foreground mb-1 block text-sm font-medium"
                      >
                        Insufficient
                      </label>
                      <AutoSaveTextarea
                        id={`sd-assertion-insufficient-${assertion.id}`}
                        value={assertion.insufficient ?? ''}
                        onChange={(value) =>
                          handleLocalUpdate(assertion.id, 'insufficient', value)
                        }
                        onSave={(value) =>
                          handleSaveField(assertion.id, 'insufficient', value)
                        }
                        debounceMs={AUTO_SAVE_DEBOUNCE_MS.slow}
                        flushOnUnmount
                        placeholder="Answers that look right and are not — the near misses this check must reject."
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-6">
                      <div>
                        <span className="text-muted-foreground mb-1 block text-sm font-medium">
                          Graded from
                        </span>
                        <AutoSaveDropdown
                          options={GRADED_FROM_OPTIONS}
                          value={assertion.gradedFrom}
                          onSave={(value) =>
                            handleSaveGradedFrom(assertion.id, value)
                          }
                          width="md"
                          // The caption above is a bare span, so it labels
                          // nothing; without this the control announces only
                          // its value, identically in every expanded row.
                          aria-label={`Graded from for ${label}`}
                        />
                      </div>

                      <AutoSaveToggle
                        checked={assertion.isBaseline}
                        onSave={(checked) =>
                          handleSaveBaseline(assertion.id, checked)
                        }
                        label="Baseline (visible mid-session)"
                        // `label` renders a bare span next to the switch, not
                        // an associated one, so it is visual only -- without
                        // this the switch has no accessible name at all, and
                        // every row's reads identically.
                        aria-label={`Baseline (visible mid-session) for ${label}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
